import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { IOrderAlertType, ORDER_LINE_ALERT_TYPES } from 'src/app/features/quotations/models/order-alert-types';
import { OrderAlertPreferencesService } from 'src/app/features/quotations/services/order-alert-preferences.service';
import {
  GetOrdersSettings,
  UpdateOrdersSettings,
  LoadSplittingRules,
  CreateSplittingRule,
  UpdateSplittingRule,
  DeleteSplittingRule,
} from '../../store/settings.actions';
import {
  IOrdersSettingsUpdate,
  IPricePolicySettings,
  OrderPriceSource,
  PRICE_SOURCE_KINDS,
  PriceSourceKind,
  ISplittingRule,
  ISplittingRuleCreate,
} from '../../models/settings.models';

interface IPriceSourceOption {
  value: OrderPriceSource;
  title: string;
  description: string;
  isDefault?: boolean;
}

/**
 * The cards are the masters. The ordered list only exists on the *Custom*
 * card; every other card is summarised by a sentence naming the order it
 * applies and the sources it leaves out — one state, one owner.
 *
 * Save writes only what was touched: opening the screen and saving without
 * touching anything writes nothing, so an unconfigured tenant is never silently
 * rewritten.
 */
@Component({
  selector: 'app-orders-settings',
  templateUrl: './orders-settings.component.html',
  styleUrls: ['./orders-settings.component.scss'],
})
export class OrdersSettingsComponent implements OnInit, OnDestroy {
  tab: 'pricing' | 'alerts' = 'pricing';

  /** The checked card, or null: inherited state, no card checked. */
  priceSource: OrderPriceSource | null = 'volta';
  hidePricingColumns = false;

  /** The alert types on offer, in display order. */
  readonly alertTypes: IOrderAlertType[] = ORDER_LINE_ALERT_TYPES;

  readonly priceSourceOptions: IPriceSourceOption[] = [
    {
      value: 'volta',
      title: 'settings.orders_settings.price_source.volta.title',
      description: 'settings.orders_settings.price_source.volta.description',
      isDefault: true,
    },
    {
      value: 'extracted',
      title: 'settings.orders_settings.price_source.extracted.title',
      description: 'settings.orders_settings.price_source.extracted.description',
    },
    {
      value: 'none',
      title: 'settings.orders_settings.price_source.none.title',
      description: 'settings.orders_settings.price_source.none.description',
    },
    {
      value: 'custom',
      title: 'settings.orders_settings.price_source.custom.title',
      description: 'settings.orders_settings.price_source.custom.description',
    },
  ];

  /**
   * Preset -> the order it names. Mirrors `price_policy.PRESET_SOURCE_MAP`.
   *
   * `volta` carries `extracted` behind `catalog`. A tenant that never opens
   * this screen keeps catalog-only — it has no preset at all and the server
   * falls through to the legacy company map.
   */
  readonly presetSources: Record<string, PriceSourceKind[]> = {
    volta: ['catalog', 'extracted'],
    extracted: ['extracted', 'catalog'],
    none: [],
  };

  /** The ordered list under the Custom card. Empty is a value: *No price*. */
  priceSources: PriceSourceKind[] = ['catalog'];
  priceFreshnessDays: Partial<Record<PriceSourceKind, number>> = {};
  /** False when the pricing module is absent — the whole block is then hidden. */
  hasPricePolicy = false;
  /** The two halves of `hasPricePolicy` — they arrive in either order. */
  private pricingEnabled = false;
  private policyFromServer: IPricePolicySettings | null = null;
  /** True when the tenant has expressed a policy; false = inherited. */
  configured = false;
  /**
   * The order composed on the Custom card, kept while the screen is open:
   * switching to a preset card and back must not lose it.
   */
  private composedOrder: PriceSourceKind[] | null = null;
  private composedWindows: Partial<Record<PriceSourceKind, number>> | null = null;
  /**
   * Save writes only what was touched. Nothing touched, nothing sent — the
   * server double-checks, but the screen is the first wall.
   */
  private policyTouched = false;
  private hideTouched = false;
  /**
   * What the server currently holds for `hide_pricing_columns`, so saving can
   * tell a real change from a restatement. The toggle above is the live value;
   * this is the one it is compared against.
   */
  private hideStored = false;

  splittingRules: ISplittingRule[] = [];
  splittingRulesLoading = false;
  drawerOpen = false;
  selectedRule: ISplittingRule | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private translate: TranslateService,
    private featureFlagService: FeatureFlagService,
    private alertPreferences: OrderAlertPreferencesService
  ) {}

  setTab(tab: 'pricing' | 'alerts'): void {
    this.tab = tab;
  }

  isAlertEnabled(type: string): boolean {
    return this.alertPreferences.isEnabled(type);
  }

  /** Applied on the spot: the alerts are read live, so there is nothing to save. */
  onAlertToggle(type: string, enabled: boolean): void {
    this.alertPreferences.setEnabled(type, enabled);
  }

  ngOnInit(): void {
    this.store.dispatch(GetOrdersSettings());
    this.store.dispatch(LoadSplittingRules());

    this.featureFlagService.loadFeatures().pipe(takeUntil(this.destroy$)).subscribe();

    this.featureFlagService.pricingEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.pricingEnabled = enabled;
        this.applyPricePolicy();
      });

    this.store
      .select((state) => state.settings.ordersSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        if (settings) {
          this.hidePricingColumns = settings.hide_pricing_columns;
          this.hideStored = settings.hide_pricing_columns;

          this.policyFromServer = settings.price_policy ?? null;
          this.applyPricePolicy(settings.default_price_source);
        }
      });

    this.store
      .select((state) => state.settings.splittingRules)
      .pipe(takeUntil(this.destroy$))
      .subscribe((rules) => {
        this.splittingRules = rules || [];
      });

    this.store
      .select((state) => state.settings.splittingRulesLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.splittingRulesLoading = loading;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** No addon, no policy: the screen falls back to the legacy company field. */
  private applyPricePolicy(defaultPriceSource?: OrderPriceSource | null): void {
    const policy = this.pricingEnabled ? this.policyFromServer : null;
    this.hasPricePolicy = !!policy;
    if (policy) {
      this.configured = policy.configured;
      // The card stays checked on read, configured or not. What protects
      // an unconfigured tenant from a silent rewrite is `policyTouched`
      // (set only by a real click) plus the server-side no-op gate.
      this.priceSource = policy.price_source_preset;
      this.priceSources = [...(policy.price_sources ?? [])];
      this.priceFreshnessDays = { ...(policy.price_freshness_days ?? {}) };
      if (policy.configured && policy.price_source_preset === 'custom') {
        this.composedOrder = [...this.priceSources];
        this.composedWindows = { ...this.priceFreshnessDays };
      }
    } else if (defaultPriceSource !== undefined) {
      this.priceSource = defaultPriceSource;
    }
  }

  /** *Custom* composes an order the legacy company field cannot hold. */
  get visiblePriceSourceOptions(): IPriceSourceOption[] {
    return this.hasPricePolicy
      ? this.priceSourceOptions
      : this.priceSourceOptions.filter((option) => option.value !== 'custom');
  }

  /**
   * Picking a preset card rewrites the list; picking *Custom* restores the
   * order composed earlier in this session, if any.
   */
  selectPriceSource(value: OrderPriceSource): void {
    if (this.priceSource === 'custom' && value !== 'custom') {
      // Leaving Custom: remember what was composed, for the way back.
      this.composedOrder = [...this.priceSources];
      this.composedWindows = { ...this.priceFreshnessDays };
    }
    this.priceSource = value;
    this.policyTouched = true;
    if (value === 'custom') {
      if (this.composedOrder) {
        this.priceSources = [...this.composedOrder];
        this.priceFreshnessDays = { ...(this.composedWindows ?? {}) };
      }
      // Without a composed order the current list simply stays — Custom
      // starts from what the screen shows, not from a blank.
    } else {
      this.priceSources = [...(this.presetSources[value] ?? [])];
      this.priceFreshnessDays = this.filteredWindows(this.priceSources);
    }
  }

  /**
   * The list is only rendered on the Custom card, so touching it never has to
   * guess a card. The card is never re-derived from the list — re-deriving is
   * what would make it jump back to a preset.
   */
  onPriceSourcesChanged(event: {
    sources: PriceSourceKind[];
    freshnessDays: Partial<Record<PriceSourceKind, number>>;
  }): void {
    this.priceSources = [...event.sources];
    this.priceFreshnessDays = { ...event.freshnessDays };
    this.composedOrder = [...event.sources];
    this.composedWindows = { ...event.freshnessDays };
    this.policyTouched = true;
  }

  private sourceLabel(kind: PriceSourceKind): string {
    return this.translate.instant(`settings.orders_settings.price_source_kind.${kind}`);
  }

  /** The sentence a preset card is summarised by. */
  get orderSummary(): string {
    // `PRICE_SOURCE_KINDS`, not a literal: a source added to the engine has to
    // show up as "left out" here without anyone remembering this line.
    const active = this.priceSources.map((kind) => this.sourceLabel(kind)).join(' → ');
    const inactive = PRICE_SOURCE_KINDS
      .filter((kind) => !this.priceSources.includes(kind))
      .map((kind) => this.sourceLabel(kind))
      .join(', ');
    const applied = active
      ? this.translate.instant('settings.orders_settings.summary.applied_order', { order: active })
      : this.translate.instant('settings.orders_settings.summary.no_source');
    const left = inactive
      ? ' ' + this.translate.instant(
          'settings.orders_settings.summary.inactive_sources', { sources: inactive })
      : '';
    return applied + left;
  }

  onHidePricingColumnsChange(value: boolean): void {
    this.hidePricingColumns = value;
    this.hideTouched = true;
  }

  /** Only what was touched travels; nothing touched, nothing sent. */
  save(): void {
    if (!this.policyTouched && !this.hideTouched) {
      return;
    }
    const settings: IOrdersSettingsUpdate = {};
    // Derived from the source, not read off the toggle: the toggle only exists
    // while "No price by default" is selected, so leaving that card takes the
    // control off the screen. A stored `true` left behind hides the pricing
    // columns of a tenant that now has a price source, with nothing on screen
    // to turn them back on — hence a source change moves this value too.
    //
    // Compared to what the server holds, not gated on `hideTouched`: that keeps
    // "only what changed travels" true while letting a card switch carry the
    // consequence it has.
    const hide = this.priceSource === 'none' ? this.hidePricingColumns : false;
    if (hide !== this.hideStored) {
      settings.hide_pricing_columns = hide;
    }
    if (this.policyTouched && this.hasPricePolicy) {
      // null clears the policy: back to the inherited state.
      settings.price_source_preset = this.priceSource;
      if (this.priceSource !== null) {
        settings.price_sources = [...this.priceSources];
        settings.price_freshness_days = this.filteredWindows(this.priceSources);
      }
      // The legacy company field keeps being written for the three values it
      // understands -- which are exactly the presets; `custom` and the
      // inherited state have no equivalent.
      if (this.priceSource && this.priceSource in this.presetSources) {
        settings.default_price_source = this.priceSource;
      }
    }
    if (this.policyTouched && !this.hasPricePolicy && this.priceSource) {
      settings.default_price_source = this.priceSource;
    }
    // The touch flags say a control was moved; they do not say the move
    // composed anything to write. A PATCH with an empty body tells the server
    // nothing and tells the user the opposite — that something was saved. The
    // payload is the only honest gate, and it is the one every future branch
    // added above passes through.
    if (!Object.keys(settings).length) {
      this.policyTouched = false;
      this.hideTouched = false;
      return;
    }
    this.store.dispatch(UpdateOrdersSettings({ settings }));
    this.policyTouched = false;
    this.hideTouched = false;
  }

  /**
   * Back to the inherited state — no card checked — and it counts as a
   * modification: saving sends the explicit null that clears the stored policy.
   *
   * Without a policy there is nothing to clear: the legacy company field has
   * no null, so the inherited state is one this tenant cannot express. Leaving
   * the screen as it is beats checking no card and saving nothing.
   */
  restoreDefaults(): void {
    if (!this.hasPricePolicy) return;
    this.priceSource = null;
    this.priceSources = [];
    this.priceFreshnessDays = {};
    this.policyTouched = true;
  }

  /**
   * Windows restricted to the used sources that can carry one: phantom windows
   * for switched-off or non-windowable sources are never re-saved.
   */
  private filteredWindows(
    used: PriceSourceKind[]
  ): Partial<Record<PriceSourceKind, number>> {
    const windows: Partial<Record<PriceSourceKind, number>> = {};
    (['observation', 'history'] as PriceSourceKind[]).forEach((kind) => {
      const days = this.priceFreshnessDays[kind];
      if (used.includes(kind) && days) {
        windows[kind] = days;
      }
    });
    return windows;
  }

  openRuleDrawer(rule?: ISplittingRule): void {
    this.selectedRule = rule ?? null;
    this.drawerOpen = true;
  }

  onSaveRule(dto: ISplittingRuleCreate): void {
    if (this.selectedRule) {
      this.store.dispatch(UpdateSplittingRule({ id: this.selectedRule.id, rule: dto }));
    } else {
      this.store.dispatch(CreateSplittingRule({ rule: dto }));
    }
    this.drawerOpen = false;
    this.selectedRule = null;
  }

  onDeleteRule(id: number): void {
    this.store.dispatch(DeleteSplittingRule({ id }));
  }

  onToggleRule(event: { id: number; active: boolean }): void {
    this.store.dispatch(UpdateSplittingRule({ id: event.id, rule: { active: event.active } }));
  }
}
